import {
  Component,
  Input,
  Output,
  EventEmitter,
  input,
  InputSignal,
  computed,
  signal,
} from "@angular/core";
import {
  DropdownDirective,
  DropDownOption,
} from "@bitwarden/web-vault/app/directives/dropdown.directive";

@Component({
  selector: "app-dropdown-multiselect",
  templateUrl: "./dropdown-multiselect.component.html",
})
export class DropdownMultiselectComponent extends DropdownDirective {
  options: InputSignal<DropDownOption[]> = input<DropDownOption[]>([]);
  @Input() selectedOptions: string[] = [];
  @Input() disabledOptions: string[] = [];
  @Input() title: string = "";
  @Input() testId: string = "";
  @Output() selectionChange: EventEmitter<string[]> = new EventEmitter<string[]>();

  sortedOptions = computed(() => {
    // Copy before sorting — Array.prototype.sort mutates in place, and options() is owned by
    // the parent binding the input, not this component.
    return [...this.options()].sort((a, b) => {
      if (!a?.name && !b?.name) {
        return 0; // both are considered equal if they have no name
      } else if (!a?.name) {
        return 1; // a is considered greater if it has no name
      } else if (!b?.name) {
        return -1; // b is considered greater if it has no name
      } else if (a?.name && b?.name) {
        return a.name.localeCompare(b.name);
      }
    });
  });

  searchTerm = signal("");

  filteredOptions = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const options = this.sortedOptions();
    if (!term) {
      return options;
    }
    return options.filter((option) => option?.name?.toLowerCase().includes(term));
  });

  // Plain method, not computed(): counts against the plain @Input() disabledOptions
  // array, which computed() can't track — a computed() here would go stale when only
  // disabledOptions changes (e.g. a country filter narrowing selectable institutions/accounts).
  selectAllText(): string {
    const selectableCount = this.selectable(this.filteredOptions()).length;
    return `Select All (${selectableCount})`;
  }

  // Plain method, not computed(): areAllOptionsSelected() reads the plain
  // @Input() selectedOptions array (mutated in place by toggleOption, reassigned by
  // selectAllOptions), which computed() can't track — a computed() here would go stale
  // on selection changes.
  hasActiveFilter(): boolean {
    return !this.areAllOptionsSelected();
  }

  onSearchChange(value: string) {
    this.searchTerm.set(value);
  }

  toggleOption(option: string) {
    if (this.isDisabled(option)) {
      return;
    }
    const index = this.selectedOptions.indexOf(option);
    if (index === -1) {
      this.selectedOptions.push(option);
    } else {
      this.selectedOptions.splice(index, 1);
    }
    this.selectionChange.emit(this.selectedOptions);
  }

  isSelected(option: string): boolean {
    return this.selectedOptions.includes(option);
  }

  // Scoped to the currently filtered (visible) options, so "Select All" only
  // acts on what the user can see when a search term is active.
  isAllDisabled(): boolean {
    return this.filteredOptions().every((option: DropDownOption) =>
      this.disabledOptions.includes(option.id),
    );
  }

  selectAllOptions() {
    if (this.isAllDisabled()) {
      return;
    }
    const visibleIds = this.filteredOptions().map((option) => option.id);
    if (this.areAllFilteredOptionsSelected()) {
      this.selectedOptions = this.selectedOptions.filter((id) => !visibleIds.includes(id));
    } else {
      const toAdd = visibleIds.filter(
        (id) => !this.disabledOptions.includes(id) && !this.selectedOptions.includes(id),
      );
      this.selectedOptions = [...this.selectedOptions, ...toAdd];
    }
    this.selectionChange.emit(this.selectedOptions);
  }

  private selectable(options: DropDownOption[]): DropDownOption[] {
    return options.filter((option) => !this.disabledOptions.includes(option.id));
  }

  // Membership against the selectable (non-disabled) options, not count-equality —
  // count-equality reads as "all selected" whenever selectedOptions holds as many ids as
  // there are selectable options, even when they aren't the same ids.
  // An empty selectable set is vacuously "all selected" here, which keeps the collapsed
  // pill reading "All <title>" for an empty or fully-disabled list, as it did before.
  areAllOptionsSelected(): boolean {
    return this.selectable(this.sortedOptions()).every((option) =>
      this.selectedOptions.includes(option.id),
    );
  }

  // The length check is what fixes #4175: with no search results there is nothing to
  // select, so the Select All row must not render checked-and-disabled.
  areAllFilteredOptionsSelected(): boolean {
    const selectable = this.selectable(this.filteredOptions());
    return (
      selectable.length > 0 &&
      selectable.every((option) => this.selectedOptions.includes(option.id))
    );
  }

  getOptionsText(): string {
    if (this.areAllOptionsSelected()) {
      return `All ${this.title}`;
    }
    const selectedCount = this.selectedOptions.length;
    return `${this.title} (${selectedCount})`;
  }

  trackByFn(index: number, item: DropDownOption): string {
    return item.id;
  }
}
